import { FC } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import LogoutIcon from '@/assets/svg/icon-logout.svg?react';
import IconUser from '@/assets/svg/icon-user.svg?react';
import MainLogo from '@/assets/svg/main-logo-flat.svg?react';

import { cn } from '@/lib/utils';

import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { BackButton } from '@/components/BackButton/BackButton';

import { useAuthStore } from '@/stores/authStore';

import { USER_ROLES } from '@/constants/global-constants';
import {
  adminRoutes,
  getPageTitle,
  publicRoutes,
  RouteNameValues,
  ROUTES,
} from '@/constants/route-settings';

export const LayoutDesktopWidget: FC = () => {
  const { signOut, user, token } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const isSettingsPage = location.pathname.includes(adminRoutes.settings);

  const userRoleTitle = () => {
    if (user === null) return null;
    return Object.values(USER_ROLES).find((role) => role.value === user.role)
      ?.title;
  };

  return (
    <div className="flex h-screen w-full overflow-hidden">
      {!!token && !!user && (
        <aside className="relative z-20 flex h-screen w-[330px] flex-shrink-0 flex-col justify-between rounded-r-[20px] bg-sidebar text-sidebar-foreground shadow-[0px_0px_20px_10px_#00000040]">
          {/* Fixed header */}
          <div className="w-full flex-shrink-0">
            <MainLogo className="mx-auto my-1.5 w-[200px] fill-[#F58426]" />
          </div>

          {/* Scrollable navigation area */}
          <div className="flex-grow overflow-y-auto">
            <NavigationMenu orientation="vertical" className="w-full">
              <NavigationMenuList className="mx-auto w-[290px] flex-col">
                {Object.values(ROUTES[user.role])
                  .filter((route) => route.isNav)
                  .map(({ path, title, pathsInclude, icon: Icon }) => (
                    <NavigationMenuItem key={title}>
                      <NavLink
                        to={path}
                        className={({ isActive }) =>
                          cn(
                            'flex w-full cursor-pointer items-center gap-3 rounded-md p-4 transition-colors',
                            isActive ||
                              pathsInclude?.some((pathInclude) =>
                                location.pathname.includes(pathInclude),
                              )
                              ? 'bg-sidebar-hover'
                              : 'hover:bg-sidebar-hover',
                          )
                        }
                      >
                        {!!Icon && (
                          <div className="relative h-[30px] min-w-[30px]">
                            <Icon className="absolute left-1/2 top-1/2 h-[30px] min-w-5 -translate-x-1/2 -translate-y-1/2 fill-white" />
                          </div>
                        )}
                        <span>{title}</span>
                      </NavLink>
                    </NavigationMenuItem>
                  ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Fixed footer with user info */}
          <div className="flex w-full flex-shrink-0 items-center justify-between border-t border-sidebar-hover p-8 pr-7">
            <div className="flex items-center gap-4">
              <div className="relative h-[30px] min-w-[30px]">
                <IconUser className="absolute left-1/2 top-1/2 h-[30px] min-w-5 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div className="flex flex-col">
                <span
                  className="max-w-[180px] truncate text-base font-semibold leading-5"
                  title={user?.username}
                >
                  {user?.username}
                </span>
                <span className="text-sm font-medium leading-none">
                  {userRoleTitle()}
                </span>
              </div>
            </div>
            <button
              className="flex cursor-pointer items-center rounded-md p-2 transition-colors hover:bg-sidebar-hover"
              onClick={() => {
                signOut();
                navigate(publicRoutes.login);
              }}
            >
              <LogoutIcon className="h-[20px] w-[26px] fill-white" />
            </button>
          </div>
        </aside>
      )}

      {/* Main content area */}
      <div className="flex flex-grow flex-col overflow-hidden">
        <nav
          className={cn(
            'flex h-[70px] min-h-[70px] items-center pl-[50px]',
            isSettingsPage ? '' : 'border-b shadow-sm',
          )}
        >
          <BackButton />
          <h1 className="text-xl font-semibold text-nav-text">
            {user !== null &&
              getPageTitle(location.pathname as RouteNameValues, user.role)}
          </h1>
        </nav>

        <div className="flex-grow overflow-auto bg-page-background">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
